import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useQuery } from 'react-query';
import axios from 'axios';
import Skeleton from '../../components/layout/Skeleton';
import { translateFilter } from './util';
import { parse, format } from 'date-fns';

// Desfechos factuais de um reengajamento, na ordem da pilha (de baixo p/ cima):
// resolvido na base (verde), os terminais "ruins" no topo (recusa/sem desfecho)
// pra saltarem à vista. Mutuamente excludentes; a altura total do bucket = chats
// cutucados que já tiveram desfecho (não inclui chats ainda pendentes no bucket).
// "Sem desfecho" (motivo reengajamento_timeout) = chat ainda com o bot que fechou
// inativo com a cutucada pendente — resíduo de legado/erro, tende a 0.
const DESFECHOS = [
  { key: 'resolvido', label: 'Resolvido', color: '#22c55e', fact: 'qtd_chats_reengajamento_resolvido' },
  { key: 'sem_resposta', label: 'Sem resposta', color: '#8b5cf6', fact: 'qtd_chats_reengajamento_sem_resposta' },
  { key: 'ajuda', label: 'Pedido de ajuda', color: '#3b82f6', fact: 'qtd_chats_reengajamento_ajuda' },
  { key: 'recusa', label: 'Recusa', color: '#f59e0b', fact: 'qtd_chats_reengajamento_recusado' },
  { key: 'timeout', label: 'Sem desfecho', color: '#ef4444', fact: 'qtd_chats_reengajamento_timeout' },
] as const;

type Granularidade = 'data_criacao_dia' | 'data_criacao_semana' | 'data_criacao_mes';

type Props = {
  periodo: [string, string, string];
  canal_id: number[];
  granularidade: Granularidade;
};

const labelDoBucket = (granularidade: Granularidade): string => {
  switch (granularidade) {
    case 'data_criacao_dia':
      return 'dia';
    case 'data_criacao_semana':
      return 'semana';
    case 'data_criacao_mes':
      return 'mês';
  }
};

const formatarBucket = (valor: string, granularidade: Granularidade): string => {
  // O backend devolve o início do bucket como timestamp (ex.: '2026-01-01T00:00:00').
  try {
    const d = parse(valor.substring(0, 10), 'yyyy-MM-dd', new Date());
    if (granularidade === 'data_criacao_mes') return format(d, 'MM/yyyy');
    return format(d, 'dd/MM');
  } catch {
    return valor;
  }
};

export const GraficoDesfechoReengajamento = ({
  periodo,
  canal_id,
  granularidade,
}: Props) => {
  const q = useQuery(
    ['desfechoReengajamento', periodo, canal_id, granularidade],
    async () => {
      const qs = new URLSearchParams();
      qs.append('facts', DESFECHOS.map((d) => d.fact).join(','));
      qs.append('dimensions', granularidade);
      qs.append('filter', translateFilter({ periodo, canal_id }));

      const res = await axios.get(
        '/api/relatorios/chat_atendimento?' + qs.toString(),
      );
      return res.data.result;
    },
    { refetchOnWindowFocus: false },
  );

  if (q.isFetching) return <Skeleton format="box" />;

  // Cada linha do backend é um bucket com as contagens de cada desfecho. Ordena
  // pelo valor bruto do bucket (timestamp) e formata o rótulo do eixo X.
  const dados = (q.data ?? [])
    .map((row: any) => ({
      bucket: formatarBucket(row[granularidade], granularidade),
      bucketSort: row[granularidade],
      ...Object.fromEntries(
        DESFECHOS.map((d) => [d.key, parseInt(row[d.fact] ?? '0', 10) || 0]),
      ),
    }))
    .sort((a: any, b: any) => (a.bucketSort < b.bucketSort ? -1 : 1));

  // Eixo Y ancorado no bucket mais alto do período: sem isso o recharts arredonda
  // o máximo para um número "redondo" acima do dado, e as áreas ficam com folga
  // no topo do gráfico.
  const maxTotal = dados.reduce(
    (max: number, row: any) =>
      Math.max(max, DESFECHOS.reduce((s, d) => s + row[d.key], 0)),
    0,
  );

  if (q.isSuccess && dados.length === 0) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <p>Nenhum dado encontrado</p>
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      <ResponsiveContainer width="100%" height={250}>
        <AreaChart
          margin={{ top: 5, right: 5, left: 0, bottom: 5 }}
          data={dados}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="bucket" interval={0} tick={{ fontSize: '0.7rem' }} />
          <YAxis
            tick={{ fontSize: '0.7rem' }}
            allowDecimals={false}
            domain={[0, maxTotal > 0 ? maxTotal : 'auto']}
          />
          <Tooltip
            cursor={{ fill: '#8882' }}
            itemStyle={{ padding: 0, fontSize: '0.85rem' }}
            labelStyle={{ fontWeight: 'bold' }}
          />
          <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
          {DESFECHOS.map((d) => (
            <Area
              key={d.key}
              type="monotone"
              stackId="a"
              dataKey={d.key}
              name={d.label}
              stroke={d.color}
              fill={d.color}
              fillOpacity={0.7}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};